'use client';

import { createClient } from '@/lib/supabase/client';

interface CityPage {
  id: string;
  name: string;
  province: string;
  slug: string;
  description: string;
  features: string[];
  coverageAreas?: string[];
  serviceBenefits?: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
  cityTestimonials?: Array<{
    name: string;
    role: string;
    rating: number;
    content: string;
    avatarUrl?: string;
  }>;
  pricingInfo?: {
    baseRate: string;
    perKm: string;
    perHelper: string;
    minimumCharge: string;
    description: string;
    features: string[];
  };
  driverOpportunities?: {
    title: string;
    description: string;
    benefits: string[];
    requirements: string[];
  };
  cityFaqs?: Array<{
    question: string;
    answer: string;
  }>;
  heroImageUrl?: string;
  ctaPrimaryText?: string;
  ctaSecondaryText?: string;
  metaTitle?: string;
  metaDescription?: string;
  status: 'active' | 'inactive';
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export const cityService = {
  async getAll(): Promise<CityPage[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('city_pages')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        name: row.name,
        province: row.province,
        slug: row.slug,
        description: row.description,
        features: row.features || [],
        metaTitle: row.meta_title,
        metaDescription: row.meta_description,
        status: row.status,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get city pages error:', error.message);
      throw error;
    }
  },

  async getActive(): Promise<Array<{
    id: string;
    name: string;
    province: string;
    slug: string;
    description: string;
    displaySettings: {
      emoji: string;
      colorGradient: string;
      borderClass: string;
    };
    displayOrder: number;
  }>> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('city_pages')
        .select('id, name, province, slug, description, display_settings, display_order')
        .eq('status', 'active')
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        name: row.name,
        province: row.province,
        slug: row.slug,
        description: row.description,
        displaySettings: {
          emoji: row.display_settings?.emoji || '🏙️',
          colorGradient: row.display_settings?.colorGradient || 'from-brand-purple/10 to-accent-purple/10',
          borderClass: row.display_settings?.borderClass || 'border-brand-purple/20',
        },
        displayOrder: row.display_order || 0,
      }));
    } catch (error: any) {
      console.error('Get active cities error:', error.message);
      throw error;
    }
  },

  async getBySlug(slug: string): Promise<CityPage | null> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('city_pages')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'active')
        .maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      if (!data) return null;

      return {
        id: data.id,
        name: data.name,
        province: data.province,
        slug: data.slug,
        description: data.description,
        features: data.features || [],
        coverageAreas: data.coverage_areas || [],
        serviceBenefits: data.service_benefits || [],
        cityTestimonials: data.city_testimonials || [],
        pricingInfo: data.pricing_info || null,
        driverOpportunities: data.driver_opportunities || null,
        cityFaqs: data.city_faqs || [],
        heroImageUrl: data.hero_image_url,
        ctaPrimaryText: data.cta_primary_text,
        ctaSecondaryText: data.cta_secondary_text,
        metaTitle: data.meta_title,
        metaDescription: data.meta_description,
        status: data.status,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Get city by slug error:', error.message);
      throw error;
    }
  },

  async create(city: Omit<CityPage, 'id' | 'createdAt' | 'updatedAt'>): Promise<CityPage | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('city_pages')
        .insert({
          name: city.name,
          province: city.province,
          slug: city.slug,
          description: city.description,
          features: city.features,
          coverage_areas: city.coverageAreas || [],
          service_benefits: city.serviceBenefits || [],
          city_testimonials: city.cityTestimonials || [],
          pricing_info: city.pricingInfo || {},
          driver_opportunities: city.driverOpportunities || {},
          city_faqs: city.cityFaqs || [],
          hero_image_url: city.heroImageUrl,
          cta_primary_text: city.ctaPrimaryText,
          cta_secondary_text: city.ctaSecondaryText,
          meta_title: city.metaTitle,
          meta_description: city.metaDescription,
          status: city.status,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        name: data.name,
        province: data.province,
        slug: data.slug,
        description: data.description,
        features: data.features || [],
        coverageAreas: data.coverage_areas || [],
        serviceBenefits: data.service_benefits || [],
        cityTestimonials: data.city_testimonials || [],
        pricingInfo: data.pricing_info || null,
        driverOpportunities: data.driver_opportunities || null,
        cityFaqs: data.city_faqs || [],
        heroImageUrl: data.hero_image_url,
        ctaPrimaryText: data.cta_primary_text,
        ctaSecondaryText: data.cta_secondary_text,
        metaTitle: data.meta_title,
        metaDescription: data.meta_description,
        status: data.status,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Create city page error:', error.message);
      throw error;
    }
  },

  async update(id: string, city: Partial<Omit<CityPage, 'id' | 'createdAt' | 'updatedAt'>>): Promise<CityPage | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const updateData: any = {};
      if (city.name !== undefined) updateData.name = city.name;
      if (city.province !== undefined) updateData.province = city.province;
      if (city.slug !== undefined) updateData.slug = city.slug;
      if (city.description !== undefined) updateData.description = city.description;
      if (city.features !== undefined) updateData.features = city.features;
      if (city.coverageAreas !== undefined) updateData.coverage_areas = city.coverageAreas;
      if (city.serviceBenefits !== undefined) updateData.service_benefits = city.serviceBenefits;
      if (city.cityTestimonials !== undefined) updateData.city_testimonials = city.cityTestimonials;
      if (city.pricingInfo !== undefined) updateData.pricing_info = city.pricingInfo;
      if (city.driverOpportunities !== undefined) updateData.driver_opportunities = city.driverOpportunities;
      if (city.cityFaqs !== undefined) updateData.city_faqs = city.cityFaqs;
      if (city.heroImageUrl !== undefined) updateData.hero_image_url = city.heroImageUrl;
      if (city.ctaPrimaryText !== undefined) updateData.cta_primary_text = city.ctaPrimaryText;
      if (city.ctaSecondaryText !== undefined) updateData.cta_secondary_text = city.ctaSecondaryText;
      if (city.metaTitle !== undefined) updateData.meta_title = city.metaTitle;
      if (city.metaDescription !== undefined) updateData.meta_description = city.metaDescription;
      if (city.status !== undefined) updateData.status = city.status;

      const { data, error } = await supabase
        .from('city_pages')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        name: data.name,
        province: data.province,
        slug: data.slug,
        description: data.description,
        features: data.features || [],
        coverageAreas: data.coverage_areas || [],
        serviceBenefits: data.service_benefits || [],
        cityTestimonials: data.city_testimonials || [],
        pricingInfo: data.pricing_info || null,
        driverOpportunities: data.driver_opportunities || null,
        cityFaqs: data.city_faqs || [],
        heroImageUrl: data.hero_image_url,
        ctaPrimaryText: data.cta_primary_text,
        ctaSecondaryText: data.cta_secondary_text,
        metaTitle: data.meta_title,
        metaDescription: data.meta_description,
        status: data.status,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Update city page error:', error.message);
      throw error;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('city_pages')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.error('Delete city page error:', error.message);
      throw error;
    }
  },

  async updateCityFaqs(cityId: string, faqs: Array<{ question: string; answer: string }>): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('city_pages')
        .update({ city_faqs: faqs })
        .eq('id', cityId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.error('Update city FAQs error:', error.message);
      throw error;
    }
  },

  async getCityFaqs(cityId: string): Promise<Array<{ question: string; answer: string }>> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('city_pages')
        .select('city_faqs')
        .eq('id', cityId)
        .single();

      if (error) {
        console.error('Get city FAQs error:', error.message);
        return [];
      }

      return data?.city_faqs || [];
    } catch (error: any) {
      console.error('Get city FAQs error:', error.message);
      return [];
    }
  },
};